// src/components/Map/CameraFOV.js
import { useEffect, useState, useMemo } from "react";
import { Source, Layer } from "react-map-gl/mapbox";
import { authFetch } from "../../../utils";

const cameraGatewayApiUrl = "https://camgateway.senticnel.com/ptz";

export default function CameraFOV({ cameraId, fovColor, center }) {
  const [pan, setPan] = useState(null);
  const [zoom, setZoom] = useState(null);
  const [valid, setValid] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const fetchStatus = async () => {
      try {
        const res = await authFetch(`${cameraGatewayApiUrl}/status/${cameraId}`);

        if (res.status === 404) {
          if (isMounted) setValid(false);
          return;
        }

        const data = await res.json();
        if (isMounted) {
          setValid(true);
          if (typeof data.pan === "number") setPan(data.pan);
          if (typeof data.zoom === "number") setZoom(data.zoom);
        }
      } catch (err) {
        console.error("Error obteniendo status:", err);
      }
    };

    fetchStatus();
    const interval = setInterval(fetchStatus, 20000);
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [cameraId]);

  // función para calcular FOV real según zoom (máx 128 niveles)
  function fovAtZoomLevel(level) {
    const maxFov = 70;
    const minFov = 1.8;
    const maxZoom = 128;
    const ratio = minFov / maxFov;
    const t = (level - 1) / (maxZoom - 1);
    return maxFov * Math.pow(ratio, t);
  }

  function hexToRgba(hex, alpha) {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  

  const geojson = useMemo(() => {
    if (!center || pan == null || zoom == null || !valid) return null;
    const [lng, lat] = center;

    const distanceMeters = 2000; // escala de alcance
    const fov = fovAtZoomLevel(zoom);
    const halfFov = fov / 2;

    function pointAt(angleDeg) {
      const rad = (angleDeg * Math.PI) / 180;
      const dx = (distanceMeters / (111320 * Math.cos(lat * Math.PI / 180))) * Math.sin(rad);
      const dy = (distanceMeters / 111320) * Math.cos(rad);
      return [lng + dx, lat + dy];
    }

    const left = pointAt(pan - halfFov);
    const right = pointAt(pan + halfFov);

    // punto medio de la base
    const midBase = [(left[0] + right[0]) / 2, (left[1] + right[1]) / 2];

    // vector del lado
    const vx = right[0] - left[0];
    const vy = right[1] - left[1];

    // ángulo del lado respecto al norte
    let angleDeg = Math.atan2(vx, vy) * 180 / Math.PI;

    // corregir para que el texto quede paralelo
    angleDeg = (angleDeg + 270) % 360;




    // vector perpendicular hacia fuera
    const perpX = -vy;
    const perpY = vx;
    const length = Math.sqrt(perpX * perpX + perpY * perpY);
    const offsetMeters = 20; // distancia del texto fuera del triángulo
    const offsetLng = (offsetMeters / (111320 * Math.cos(lat * Math.PI / 180))) * (perpX / length);
    const offsetLat = (offsetMeters / 111320) * (perpY / length);
    const labelPos = [midBase[0] + offsetLng, midBase[1] + offsetLat];

    return {
      type: "FeatureCollection",
      features: [
        {
          type: "Feature",
          geometry: {
            type: "Polygon",
            coordinates: [[[lng, lat], left, right, [lng, lat]]]
          }
        },
        {
          type: "Feature",
          geometry: { type: "Point", coordinates: labelPos },
          properties: { label: cameraId, rotation: angleDeg }
        }
      ]
    };
  }, [center, pan, zoom, valid, cameraId]);

  if (!geojson) return null;

  return (
    <Source id={`camera-fov-src-${cameraId}`} type="geojson" data={geojson}>
    <Layer
        id={`camera-fov-layer-${cameraId}`}
        type="fill"
        paint={{
            // relleno semitransparente: usamos el fovColor y le añadimos alpha
            "fill-color": hexToRgba(fovColor, 0.15), // hex con alpha (80 = ~50% transparencia)
            "fill-outline-color": fovColor
        }}
        />

      <Layer
            id={`camera-fov-label-${cameraId}`}
            type="symbol"
            layout={{
                "text-field": ["get", "label"],
                "text-size": 14,
                "text-anchor": "center",
                "text-rotation-alignment": "map",
                "text-rotate": ["get", "rotation"]
            }}
            paint={{
                "text-color": "#ffffff",
                "text-halo-color": "#000000",  // borde negro
                "text-halo-width": 2           // grosor del borde
            }}
            />


    </Source>
  );
}
